import React, { useState, useEffect } from 'react';

// Inject animation keyframes once
let _kfInjected = false;
function injectKF() {
  if (_kfInjected || typeof document === 'undefined') return;
  _kfInjected = true;
  const s = document.createElement('style');
  s.id = 'athena-focus-kf';
  s.textContent = `
    @keyframes focusPulse { 0%,100%{opacity:1}50%{opacity:.35} }
    @keyframes focusWinIn {
      from{opacity:0;transform:translateX(-50%) translateY(10px)}
      to{opacity:1;transform:translateX(-50%) translateY(0)}
    }
    @keyframes focusCardIn {
      from{opacity:0;transform:translateY(-6px)}
      to{opacity:1;transform:translateY(0)}
    }
  `;
  document.head.appendChild(s);
}

const T = {
  surface: 'rgba(10,16,34,0.93)',
  border:  'rgba(56,189,248,0.14)',
  teal:    '#38bdf8',
  tealDim: 'rgba(56,189,248,0.65)',
  tealBg:  'rgba(56,189,248,0.09)',
  text:    '#e2e8f0',
  muted:   'rgba(148,163,184,0.62)',
  win:     '#34d399',
};

// ── Progress bar ──────────────────────────────────────────────────────────────
function FocusProgressBar({ progress }) {
  return (
    <div style={{ height: 3, background: 'rgba(56,189,248,0.08)', borderRadius: 2, overflow: 'hidden' }}>
      <div style={{
        height: '100%', width: `${Math.max(progress, 0)}%`,
        background: 'linear-gradient(90deg, #38bdf8, #818cf8)',
        borderRadius: 2,
        transition: 'width .7s cubic-bezier(.4,0,.2,1)',
      }} />
    </div>
  );
}

// ── Microvitória toast (fixed, appears briefly) ────────────────────────────────
export function FocusWinToast({ win, onDone }) {
  useEffect(() => {
    injectKF();
    if (!win) return;
    const t = setTimeout(onDone, 3200);
    return () => clearTimeout(t);
  }, [win, onDone]);

  if (!win) return null;
  return (
    <div style={{
      position: 'fixed', bottom: 112, left: '50%', transform: 'translateX(-50%)',
      background: T.surface, border: `1px solid rgba(52,211,153,0.22)`,
      borderRadius: 14, padding: '8px 20px',
      display: 'flex', alignItems: 'center', gap: 9,
      zIndex: 9010, backdropFilter: 'blur(14px)',
      boxShadow: '0 8px 28px rgba(0,0,0,0.45)',
      animation: 'focusWinIn .3s ease',
      pointerEvents: 'none', whiteSpace: 'nowrap',
    }}>
      <span style={{ fontSize: 13, color: T.win }}>✓</span>
      <span style={{ fontSize: 12.5, color: T.win, letterSpacing: '.01em' }}>{win}</span>
    </div>
  );
}

// ── Mission card (fixed top-right, collapsible) ────────────────────────────────
export function FocusMissionCard({ mission, onAdvance, onDismiss }) {
  const [collapsed, setCollapsed] = useState(false);
  useEffect(() => { injectKF(); }, []);

  if (!mission) return null;
  const done = mission.progress >= 100;
  const currentLabel = mission.steps[mission.currentStep] || '';
  const nextLabel = mission.steps[mission.currentStep + 1];

  return (
    <div style={{
      position: 'fixed', top: 68, right: 14,
      width: collapsed ? 40 : 250,
      background: T.surface,
      border: `1px solid ${T.border}`,
      borderRadius: 16,
      backdropFilter: 'blur(16px)',
      zIndex: 600,
      overflow: 'hidden',
      transition: 'width .22s ease',
      boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
      animation: 'focusCardIn .25s ease',
    }}>
      {/* Header row */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: collapsed ? '11px 12px' : '10px 12px',
      }}>
        <div style={{
          width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
          background: done ? T.win : T.teal,
          boxShadow: `0 0 5px ${done ? T.win : T.teal}`,
          animation: done ? 'none' : 'focusPulse 2.4s infinite',
        }} />
        {!collapsed && (
          <span style={{
            flex: 1, fontSize: 11, fontWeight: 600, color: T.text,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            letterSpacing: '.01em',
          }}>
            {mission.title}
          </span>
        )}
        <button
          onClick={() => setCollapsed(v => !v)}
          title={collapsed ? 'Expandir' : 'Minimizar'}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: T.muted, padding: 2, lineHeight: 1, fontSize: 11, flexShrink: 0,
          }}
        >
          {collapsed ? '◂' : '▸'}
        </button>
      </div>

      {!collapsed && (
        <div style={{ padding: '0 12px 12px' }}>
          {/* Progress */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7 }}>
            <div style={{ flex: 1 }}><FocusProgressBar progress={mission.progress} /></div>
            <span style={{
              fontSize: 10, color: T.tealDim, flexShrink: 0,
              fontVariantNumeric: 'tabular-nums', minWidth: 28, textAlign: 'right',
            }}>
              {mission.progress}%
            </span>
          </div>

          {/* Current step */}
          <div style={{ fontSize: 11, color: T.muted, lineHeight: 1.4, marginBottom: 2 }}>
            <span style={{ color: T.tealDim, marginRight: 4 }}>●</span>
            {currentLabel}
          </div>

          {/* Next step */}
          {nextLabel && !done && (
            <div style={{
              fontSize: 10.5, color: 'rgba(148,163,184,0.38)',
              borderTop: '1px solid rgba(56,189,248,0.07)',
              paddingTop: 5, marginTop: 5,
            }}>
              Próximo: {nextLabel}
            </div>
          )}

          {done && (
            <div style={{ fontSize: 11, color: T.win, marginTop: 4 }}>
              🎯 Missão concluída!
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: 5, marginTop: 10 }}>
            {!done && (
              <button onClick={onAdvance} style={{
                flex: 1, fontSize: 10, padding: '5px 7px',
                background: T.tealBg, border: `1px solid ${T.border}`,
                color: T.tealDim, borderRadius: 8, cursor: 'pointer',
              }}>
                Avançar ›
              </button>
            )}
            <button onClick={onDismiss} style={{
              fontSize: 10, padding: '5px 8px',
              background: 'none', border: '1px solid rgba(255,255,255,0.06)',
              color: T.muted, borderRadius: 8, cursor: 'pointer',
            }}>
              {done ? 'Fechar' : 'Pausar'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Resume prompt (inline sticky, shown at top of chat area) ──────────────────
export function FocusResumePrompt({ mission, onResume, onDismiss }) {
  useEffect(() => { injectKF(); }, []);
  if (!mission || mission.dismissed >= 3 || mission.progress >= 100) return null;

  return (
    <div style={{
      maxWidth: 740, width: '100%', margin: '8px auto 0',
      padding: '0 20px', boxSizing: 'border-box',
    }}>
      <div style={{
        background: T.surface, border: `1px solid ${T.border}`,
        borderRadius: 14, padding: '10px 16px',
        display: 'flex', alignItems: 'center', gap: 12,
        backdropFilter: 'blur(10px)',
        animation: 'focusCardIn .25s ease',
      }}>
        <div style={{
          width: 6, height: 6, borderRadius: '50%',
          background: T.teal, flexShrink: 0,
          animation: 'focusPulse 2s infinite',
        }} />
        <span style={{ flex: 1, fontSize: 12.5, color: T.text, lineHeight: 1.4 }}>
          Você estava em:{' '}
          <span style={{ color: T.tealDim, fontWeight: 600 }}>{mission.title}</span>
          {mission.progress > 0 && (
            <span style={{ color: T.muted, fontSize: 11, marginLeft: 6 }}>
              ({mission.progress}% concluído)
            </span>
          )}
        </span>
        <button onClick={onResume} style={{
          fontSize: 11.5, padding: '5px 14px',
          background: T.tealBg, border: `1px solid ${T.border}`,
          color: T.tealDim, borderRadius: 8, cursor: 'pointer', flexShrink: 0,
        }}>
          Continuar
        </button>
        <button onClick={onDismiss} style={{
          fontSize: 14, padding: '2px 5px',
          background: 'none', border: 'none',
          color: T.muted, cursor: 'pointer', flexShrink: 0, lineHeight: 1,
        }}>
          ✕
        </button>
      </div>
    </div>
  );
}
